'use client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import type { ElasticFAQItem } from '@//app/api/faq';
import { Accordion } from '@//components/Accordion';
import { Alert } from '@//components/Alert';
import useFaq from '@//data/search/faq/useFaq';
import { AccordionList } from '../../containers/AccordionList';
import { FaqSearchBar } from '../../containers/FaqSearchBar';

interface FaqSearchInputProps {
  initialQuery?: string;
  initialResults?: ElasticFAQItem[];
}

export const FaqSearch = ({ initialQuery, initialResults }: FaqSearchInputProps) => {
  const t = useTranslations('pages.faq');
  const searchParams = useSearchParams();
  const [query, setQuery] = useState<string>(initialQuery || '');
  const [results, setResults] = useState(initialResults);
  const faqQuery = useFaq(searchParams);
  const data = faqQuery.data;

  useEffect(() => {
    if (data) {
      setResults(data.documentList.documents);
    }
  }, [data]);

  useEffect(() => {
    const queryParam = searchParams.get('q');
    if (queryParam && queryParam !== query) {
      setQuery(queryParam);
    }
  }, [searchParams]);

  return (
    <>
      <section aria-label={t('sections.searchInput')}>
        <FaqSearchBar />
      </section>
      <section id="faq-search-results" aria-label={t('sections.searchResults')}>
        {results && results.length > 0 ? (
          <AccordionList>
            {results.map((item) => (
              <li key={item.id}>
                <Accordion summary={item.question} testId={`accordion-${item.id}`}>
                  {item.answer}
                </Accordion>
              </li>
            ))}
          </AccordionList>
        ) : (
          query && (
            <Alert id="faq-no-results" variant="info" heading={t('hero.noResultsTitle')}>
              {t('hero.noResultsDescription')}
            </Alert>
          )
        )}
      </section>
    </>
  );
};
